import { t } from "@travily/i18n";
import { Wizard } from "@ui/components/Wizard";
import type { KeyboardEvent, ReactNode } from "react";
import { TaglineField, taglineFieldCopy } from "@/components/ListingFields/TaglineField";

const COLOR = "green" as const;
const LISTING_TYPE = "product" as const;

export interface ProductTaglineStepViewProps {
  /** Wizard progress: 1-based index of the current stage. */
  currentStage: number;
  /** Wizard progress: total number of stages. */
  totalStages: number;
  /** Navigate to the previous stage. Omit to hide the back button. */
  onBack?: () => void;
  /** Top-bar tertiary actions (e.g. "Questions?", "Finish later"). */
  topBarActions: ReactNode;
  /** Advance to / submit the current stage. */
  onPrimary: () => void;
  /** Whether the primary button shows a pending spinner. */
  primaryPending: boolean;
  /** Field error for the tagline input, or undefined when valid. */
  error?: string;
  /** Current value of the tagline textarea. */
  tagline: string;
  /** Called with the new value on every textarea change. */
  onTaglineChange: (value: string) => void;
  /** Called when the textarea loses focus (drives blur-time validation). */
  onTaglineBlur: () => void;
  /** Key handler for the textarea; Enter advances the wizard. */
  onTaglineKeyDown: (event: KeyboardEvent) => void;
}

export function ProductTaglineStepView({
  currentStage,
  totalStages,
  onBack,
  topBarActions,
  onPrimary,
  primaryPending,
  error,
  tagline,
  onTaglineChange,
  onTaglineBlur,
  onTaglineKeyDown,
}: ProductTaglineStepViewProps) {
  const { heading, helper } = taglineFieldCopy(LISTING_TYPE);

  return (
    <Wizard color={COLOR} currentStage={currentStage} totalStages={totalStages} onBack={onBack}>
      {topBarActions}
      <Wizard.PrimaryButton onPress={onPrimary} isPending={primaryPending}>
        {t("common.actions.continue")}
      </Wizard.PrimaryButton>
      <Wizard.Stage heading={heading} helper={helper}>
        <div className="w-full max-w-[600px]">
          <TaglineField
            error={error}
            listingType={LISTING_TYPE}
            onBlur={onTaglineBlur}
            onChange={onTaglineChange}
            onKeyDown={onTaglineKeyDown}
            value={tagline}
          />
        </div>
      </Wizard.Stage>
    </Wizard>
  );
}
